import { usePostHog } from "@posthog/react";
import { useEffect, useRef } from "react";

import { useAuth } from "../contexts/AuthContext";

const HEARTBEAT_POLL_INTERVAL_MS = 30_000;
const HEARTBEAT_PERIOD_S = 300;

const EVENT = {
  SESSION_START: "case_review_session_start",
  HEARTBEAT: "case_review_heartbeat",
  SESSION_END: "case_review_session_end",
} as const;

export const useCaseReviewTracking = (caseId: string | undefined): void => {
  const posthog = usePostHog();
  const { user, firmContext } = useAuth();

  const accumulatedRef = useRef(0);
  const lastActiveStartRef = useRef<number | null>(null);
  const sessionIdRef = useRef(crypto.randomUUID());
  const sessionEndFiredRef = useRef(false);

  // Primitive deps prevent effect re-runs (and session resets) when only
  // object identity changes but values stay the same.
  const userId = user?.id;
  const firmId = firmContext?.firmId;
  const roleName = firmContext?.roleName;

  useEffect(() => {
    if (!caseId || !userId || !posthog) return;

    const buildProperties = (durationSeconds: number): Record<string, unknown> => ({
      case_id: caseId,
      user_id: userId,
      duration_seconds: durationSeconds,
      session_id: sessionIdRef.current,
      ...(firmId && { firm_id: firmId, role: roleName }),
    });

    accumulatedRef.current = 0;
    sessionIdRef.current = crypto.randomUUID();
    sessionEndFiredRef.current = false;
    lastActiveStartRef.current = document.visibilityState === "visible" ? Date.now() : null;

    posthog.capture(EVENT.SESSION_START, {
      case_id: caseId,
      user_id: userId,
      session_id: sessionIdRef.current,
      ...(firmId && { firm_id: firmId, role: roleName }),
    });

    const pause = () => {
      if (lastActiveStartRef.current !== null) {
        accumulatedRef.current += (Date.now() - lastActiveStartRef.current) / 1000;
        lastActiveStartRef.current = null;
      }
    };

    const resume = () => {
      if (lastActiveStartRef.current === null) {
        lastActiveStartRef.current = Date.now();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        pause();
      } else {
        resume();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const interval = setInterval(() => {
      const liveSeconds =
        lastActiveStartRef.current !== null ? (Date.now() - lastActiveStartRef.current) / 1000 : 0;
      const totalActive = accumulatedRef.current + liveSeconds;
      const heartbeatCount = Math.floor(totalActive / HEARTBEAT_PERIOD_S);

      if (heartbeatCount > 0) {
        for (let i = 0; i < heartbeatCount; i++) {
          posthog.capture(EVENT.HEARTBEAT, buildProperties(HEARTBEAT_PERIOD_S));
        }
        accumulatedRef.current = totalActive % HEARTBEAT_PERIOD_S;
        if (lastActiveStartRef.current !== null) {
          lastActiveStartRef.current = Date.now();
        }
      }
    }, HEARTBEAT_POLL_INTERVAL_MS);

    const fireSessionEnd = (transport?: "sendBeacon") => {
      if (sessionEndFiredRef.current) return;
      sessionEndFiredRef.current = true;

      pause(); // flush live time into accumulated

      const remaining = Math.round(accumulatedRef.current);
      if (remaining > 0) {
        if (transport) {
          posthog.capture(EVENT.SESSION_END, buildProperties(remaining), { transport });
        } else {
          posthog.capture(EVENT.SESSION_END, buildProperties(remaining));
        }
      }
    };

    const handleBeforeUnload = () => fireSessionEnd("sendBeacon");
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      clearInterval(interval);
      fireSessionEnd();
    };
  }, [caseId, userId, firmId, roleName, posthog]);
};
